import { useState } from "react";
import { useRouter } from "next/compat/router";
import {
  Text,
  Button,
  Box,
  ErrorText,
  Heading,
  Modal,
} from "@cruk/cruk-react-components";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { fetcher } from "@fwa/src/services/apiClient";
import {
  fwsUrlTeamMember,
  fwsUrlTeamMemberPromote,
  fwsUrlTeamMembers,
} from "@fwa/src/services/teamService";
import { fwsUrlPageParentMemberships } from "@fwa/src/services/fundraisingPageService";

import { isPast } from "@fwa/src/utils/timeUtils";
import { userNameForPage } from "@fwa/src/utils/pageUtils";

import { MemberFundraisingPage } from "@fwa/src/components/MemberFundraisingPage";
import { FormActions } from "@fwa/src/components/FormActions";

import {
  type FwsResponseData,
  type FundraisingPageType,
  type TeamMemberType,
} from "@fwa/src/types";

export const MemberFundraisingPageSelectLeader = ({
  fundraisingPage,
  isLeader = false,
  teamId,
}: {
  fundraisingPage: FundraisingPageType;
  isLeader?: boolean;
  teamId: string;
}) => {
  const { trackError } = useTracking();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSelectNewLeaderModal, setShowSelectNewLeaderModal] =
    useState(false);
  const router = useRouter();
  const name = userNameForPage(fundraisingPage);
  const isPageClosed =
    !!fundraisingPage.closeDate && isPast(new Date(fundraisingPage.closeDate));

  const closeSelectNewLeaderModal = () => {
    setErrorMessage("");
    setIsLoading(false);
    setShowSelectNewLeaderModal(false);
  };

  const openSelectNewLeaderModal = () => {
    setShowSelectNewLeaderModal(true);
  };

  const handleChooseClick = () => {
    handleChoose().catch((err) => {
      trackError(err as Error, {
        component: "MemberFundraisingPageSelectLeader",
      });
    });
  };

  const getFundraisingPageMembershipForTeam = async (
    pageId: string,
  ): Promise<TeamMemberType | void> => {
    const fundraisingPageMembershipForTeam: TeamMemberType | void =
      await fetcher(
        fwsUrlPageParentMemberships({
          pageId,
        }),
      )
        .then((res) => res as FwsResponseData<TeamMemberType>)
        .then((res) => res.results)
        .then((memberships) => {
          const membershipToPromote: TeamMemberType | undefined =
            memberships.find(
              (teamMember: TeamMemberType) =>
                teamMember.parentPage?.uniqueId === teamId,
            );
          return membershipToPromote;
        })
        .catch((err) => {
          trackError(err as Error, {
            component: "MemberFundraisingPageEdit",
          });
          setIsLoading(false);
          setErrorMessage("unable find page membership");
        });
    return fundraisingPageMembershipForTeam;
  };

  const getCurrentTeamLeader = async (): Promise<TeamMemberType | void> => {
    const currentTeamLeader = await fetcher(
      fwsUrlTeamMembers({
        teamId,
        currentPage: 1,
        itemsPerPage: 1,
        sort: "leader",
      }),
    )
      .then((res) => res as FwsResponseData<TeamMemberType>)
      .then((res) => res.results)
      .then((memberships) => {
        const oldLeader: TeamMemberType | undefined = memberships.find(
          (teamMember: TeamMemberType) =>
            teamMember.membershipStatus === "leader",
        );
        return oldLeader;
      })
      .catch((err) => {
        trackError(err as Error, {
          component: "MemberFundraisingPageSelectLeader",
        });
        setIsLoading(false);
        setErrorMessage("current team leader not found");
      });
    return currentTeamLeader;
  };

  const handleChoose = async () => {
    setErrorMessage("");
    setIsLoading(true);

    if (!fundraisingPage) {
      setIsLoading(false);
      setErrorMessage("unable find page");
      return undefined;
    }

    // Get old team leader
    const oldLeader = await getCurrentTeamLeader();

    if (!oldLeader) {
      setIsLoading(false);
      setErrorMessage("leader not found");
      return undefined;
    }
    // get membeship for fundraising page
    const membershipForFundraisingPage =
      await getFundraisingPageMembershipForTeam(fundraisingPage.uniqueId);

    if (!membershipForFundraisingPage) {
      setIsLoading(false);
      setErrorMessage("page membership not found");
      return undefined;
    }

    // promote page membership to be team leader
    await fetcher(
      fwsUrlTeamMemberPromote({
        memberId: membershipForFundraisingPage?.uniqueId || "",
      }),
      {
        method: "POST",
      },
    ).catch((err) => {
      trackError(err as Error, {
        component: "MemberFundraisingPageSelectLeader",
      });
      setIsLoading(false);
      setErrorMessage("unable to promote member");
    });

    // remove previous leader membership
    await fetcher(
      fwsUrlTeamMember({
        memberId: oldLeader?.uniqueId || "",
      }),
      {
        method: "DELETE",
      },
    ).catch((err) => {
      trackError(err as Error, {
        component: "MemberFundraisingPageSelectLeader",
      });
      setIsLoading(false);
      setErrorMessage("unable to remove old leader");
    });

    setIsLoading(false);
    setShowSelectNewLeaderModal(false);

    // re-direct to dashboard page
    router?.push("/fundraiser/dashboard").catch((err) => {
      trackError(err as Error, {
        component: "MemberFundraisingPageSelectLeader",
      });
    });

    return undefined;
  };

  return (
    <>
      {fundraisingPage && (
        <MemberFundraisingPage
          fundraisingPage={fundraisingPage}
          isLeader={isLeader}
        >
          <Box paddingVertical="xs" margin="none">
            {isLeader ? (
              <Text style={{ lineHeight: "2.5rem" }} textAlign="center">
                Leader
              </Text>
            ) : isPageClosed ? (
              <Text style={{ lineHeight: "2.5rem" }} textAlign="center">
                Page Closed
              </Text>
            ) : (
              <Button
                full
                aria-label={`Choose ${name} as team leader`}
                onClick={() => {
                  openSelectNewLeaderModal();
                }}
                data-cta-type="choose-leader"
              >
                Choose
              </Button>
            )}
          </Box>
        </MemberFundraisingPage>
      )}
      {showSelectNewLeaderModal && (
        <Modal
          closeFunction={closeSelectNewLeaderModal}
          modalName="leave team and select a new leader"
          startOpen
        >
          <Box>
            <Heading h2>
              {`Are you sure you wish to leave the team and make ${name} the
              new team leader?`}
            </Heading>

            {!!errorMessage.length && (
              <Box>
                <ErrorText>{errorMessage}</ErrorText>
              </Box>
            )}
            <Box>
              <FormActions
                cancelLabel="Stay in team"
                submitLabel="Leave team"
                onCancel={closeSelectNewLeaderModal}
                isLoading={isLoading}
                onSubmit={handleChooseClick}
                submitButtonType="button"
              />
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default MemberFundraisingPageSelectLeader;
